import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import * as PUBLIC_API from "../../../../../../app/public_api";
import { AxiosError } from "axios";
import {
  CreationsGETRequest,
  CreationsGETResponse,
  CreationYearsStatsGETRequest,
  CreationYearsStatsGETResponse,
} from "../../../../../../utils-creation/types";
import * as T from "../../../../../../utils/types";
import { COUNT_PER_PAGE } from "../../../../../../utils-creation/constants";

export interface CreationTabState {
  creationsList: T.ResultItem[];
  status: T.statusType;
  total: number;
  currentPage: number;
  hasNext: boolean;
  errorMsg: T.errorMsgType;
  yearFilter: {
    total: number;
    years: { year: number; count: number }[];
    status: T.statusType;
    errorMsg: T.errorMsgType;
    currentRequestId: T.requestIdType;
  };
  currentRequestId: T.requestIdType;
}

const initialState: CreationTabState = {
  creationsList: [],
  status: "idle",
  total: -1,
  currentPage: 0,
  hasNext: false,
  errorMsg: null,
  yearFilter: {
    total: -1,
    years: [],
    status: "idle",
    errorMsg: null,
    currentRequestId: null,
  },
  currentRequestId: null,
};

export const initList = createAsyncThunk(
  "searchPage/creationTab/initList",
  async (data: CreationsGETRequest, thunkAPI) => {
    try {
      const { keywordType, searchInput, facultyID, departmentID, year } = data;
      const { currentPage } = (thunkAPI.getState() as T.RootState).creationTab;
      return await PUBLIC_API.getCreationsList({
        keywordType,
        searchInput,
        facultyID,
        departmentID,
        year,
        page: currentPage + 1,
      });
    } catch (err) {
      console.error((err as AxiosError)?.response?.data);
      return err;
    }
  }
);

export const loadMore = createAsyncThunk(
  "searchPage/creationTab/loadMore",
  async (data: undefined, thunkAPI) => {
    try {
      const {
        keywordType,
        searchInput,
        facultyID,
        departmentID,
        creationSpecific: { year },
      } = (thunkAPI.getState() as T.RootState).searchPgUserInputs;
      const { currentPage } = (thunkAPI.getState() as T.RootState).creationTab;
      return await PUBLIC_API.getCreationsList({
        keywordType,
        searchInput,
        facultyID,
        departmentID,
        year,
        page: currentPage + 1,
      });
    } catch (err) {
      console.error((err as AxiosError)?.response?.data);
      return err;
    }
  }
);

export const getCreationYearsStats = createAsyncThunk(
  "searchPage/creationTab/getCreationYearsStats",
  async (data: CreationYearsStatsGETRequest, thunkAPI) => {
    try {
      const { keywordType, searchInput, facultyID, departmentID } = data;
      return await PUBLIC_API.getCreationYearsStats({
        keywordType,
        searchInput,
        facultyID,
        departmentID,
      });
    } catch (err) {
      console.error((err as AxiosError)?.response?.data);
      return err;
    }
  }
);

const creationTabSlice = createSlice({
  name: "searchPage/creationTab",
  initialState,
  reducers: {},
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      // initList
      .addCase(initList.pending, (state, action) => {
        state.status = "loading";
        state.total = initialState.total;
        state.hasNext = initialState.hasNext;
        state.currentPage = initialState.currentPage;
        state.creationsList = initialState.creationsList;
        state.currentRequestId = action.meta.requestId;

        // set the list to be COUNT_PER_PAGE skeleton items
        state.creationsList = [...new Array(COUNT_PER_PAGE)].map(() => ({
          loading: true,
          item: null,
        }));
      })
      .addCase(initList.fulfilled, (state, action) => {
        // below line makes sure the response comes from the most recent request
        if (action.meta.requestId === state.currentRequestId) {
          const { links, total_result, results } =
            action.payload as CreationsGETResponse;

          state.status = "succeeded";
          state.total = total_result;
          state.hasNext = !!links?.next;
          state.currentPage += 1;

          // set the list to what we received
          state.creationsList = results?.map((x) => ({
            loading: false,
            item: x,
          }));
        }
      })
      .addCase(initList.rejected, (state, action) => {
        // below line makes sure the response comes from the most recent request
        if (action.meta.requestId === state.currentRequestId) {
          state.status = "failed";
          state.errorMsg = action.error.message;
        }
      })
      // loadMore
      .addCase(loadMore.pending, (state) => {
        state.status = "loading";

        // add COUNT_PER_PAGE skeleton items to the list
        state.creationsList = state.creationsList.concat(
          [...new Array(COUNT_PER_PAGE)].map(() => ({
            loading: true,
            item: null,
          }))
        );
      })
      .addCase(
        loadMore.fulfilled,
        (state, action: PayloadAction<CreationsGETResponse | unknown>) => {
          const { links, total_result, results } =
            action.payload as CreationsGETResponse;

          state.status = "succeeded";
          state.total = total_result;
          state.hasNext = !!links.next;
          state.currentPage += 1;

          // replace the last cntPerPage skeleton item list
          // to the list we received
          state.creationsList = state.creationsList
            .slice(0, -COUNT_PER_PAGE)
            .concat(results?.map((x) => ({ loading: false, item: x })));
        }
      )
      .addCase(loadMore.rejected, (state, action) => {
        state.status = "failed";
        state.errorMsg = action.error.message;
      })
      // getCreationYearsStats
      .addCase(getCreationYearsStats.pending, (state, action) => {
        state.yearFilter.status = "loading";
        state.yearFilter.currentRequestId = action.meta.requestId;
      })
      .addCase(getCreationYearsStats.fulfilled, (state, action) => {
        // below line makes sure the response comes from the most recent request
        if (action.meta.requestId === state.yearFilter.currentRequestId) {
          state.yearFilter.status = "succeeded";

          const { total, years } =
            action.payload as CreationYearsStatsGETResponse;

          state.yearFilter.years = years;
          state.yearFilter.total = total;
        }
      })
      .addCase(getCreationYearsStats.rejected, (state, action) => {
        if (action.meta.requestId === state.yearFilter.currentRequestId) {
          state.yearFilter.status = "failed";
          state.yearFilter.errorMsg = action.error.message;
        }
      });
  },
});

// export const { resetState, setYear } = creationTabSlice.actions;

export default creationTabSlice;
